import React, { useMemo, useEffect, useState, useCallback } from "react";
import { FormGroup, Input } from "reactstrap";
import Select, { components } from 'react-select'
import _, { assign } from "lodash";

import Notification from "../Notification";
import Loader from "../Loader";
import { useSideChannelSubscription } from "../../util/useSideChannel";
import { getObject, concatenatePaths } from "../../util/mapObject";
import getPathFromId from "../../util/getPathFromId";
import { useJnx } from "../../util/jnx";
import useOptionsLookup from "./hooks/useOptionsLookup";
import CollapsableTreeSelect, { makeTreeNode, organizeTreeNodes, visitTrees } from "../CollapsableTreeSelect";
import { convertIfNumber } from "../JsonTreeEditor/util";
import { useDebouncedEffect } from "../../hooks/useDebounceEffect";

function LookupFormField(props) {
    const {
        formData,
        formContext,
        formContext: {
            sideChannel,
            bindings: fcBindings,
            setFormDataValues,
        },
        disabled,
        readonly: propReadonly,
        schema: {
            title,
            lookup,
            type
        },
        idSchema: { $id } = {},
        uiSchema: {
            'akc:requiredIfVisible': akcRequiredIfVisible,
            'ui:readonly': uiReadonly,
            'ui:readonlyIf': uiReadonlyIf,
            'ui:onSelect': onSelectExpr,
            'ui:onChange': onChangeExpr,
            'ui:search': uiSearch
        },
        // title,
        required: requiredProp,
        onChange: propOnChange
    } = props;
    const readonly = propReadonly || uiReadonly;
    const required = requiredProp || akcRequiredIfVisible;
    const {
        resource = "Options",
        setObjectAs,
        allowNoValue = true,
        noValue = '',
        parentId,
        collapsable, collapseLevel,
        label: labelExpr,
        id: idExpr,
        firstSelected = false,
        defaultValue: defaultValueExpr,
    } = lookup || {};
    const dataPath = useMemo(() => getPathFromId($id), [$id]);

    const objectPath = useMemo(
        () => setObjectAs ? concatenatePaths(dataPath, `..${setObjectAs}`) : null,
        [dataPath, setObjectAs]
    );
    const functionBinds = useMemo(() => ({ set: setFormDataValues }), [setFormDataValues]);
    const onSelectJnx = useJnx(onSelectExpr, { functionBinds });
    const onChangeJnx = useJnx(onChangeExpr, { functionBinds });

    const [rootFormData, formObject] = useSideChannelSubscription(sideChannel, [0, 1]) || [{}, {}];

    const {
        options: lookupOptions, loadingOptions, errorLoadingOptions
    } = useOptionsLookup({
        lookup,
        rootFormData,
        formObject,
        path: dataPath,
        formContext
    });

    const bindings = useMemo(() => ({
        ...fcBindings,
        formObject,
        formContext
    }), [fcBindings, formObject, formContext]);

    const labelJnx = useJnx(labelExpr);
    const idJnx = useJnx(idExpr);

    const options = _.sortBy((useParsedOptions(lookupOptions, lookup) || []).map((option, idx) => {
        return {
            ...option,
            value: idJnx ? idJnx.eval(option.item) : option.id,
            label: labelJnx ? labelJnx.eval(option.item) : option.label
        }
    }), [(o)=> o.label?.toLowerCase()]);
    
    const containerStyle = useMemo(() => {
        const style = {}
        if (props.schema['ui:minWidth']) {
            style.minWidth = props.schema['ui:minWidth']
        }
        return style
    })

    const defaultValueJnx = useJnx(defaultValueExpr);
    const defaultValue = useMemo(() => (
        defaultValueJnx && defaultValueJnx.eval(rootFormData || {}, '', {
            root: rootFormData,
            formContext,
        })
    ), [rootFormData, formContext, defaultValueJnx]);

    // Expand with other converters later
    // Check if it SHOULD be converted, then if it CAN be converted.
    const parseFormData = (formData) => {
        switch (type) {
            case "number": {
                return convertIfNumber(formData);
            }
            default: {
                return formData;
            }
        }
    }

    const [dataValue, setDataValue] = useState(parseFormData(defaultValue || formData));
    const [clickFunction, setClickFunction] = useState(false);
    useEffect(() => {
        if (formData !== undefined && formData !== null) {
            setDataValue(parseFormData(formData))
        }
    }, [formData])

    useDebouncedEffect(() => {
        propOnChange(dataValue);
    }, [dataValue], 50);
   

    const readonlyIfJnx = useJnx(uiReadonlyIf);
    const readOnlyIf = useMemo(() => (
        readonlyIfJnx && readonlyIfJnx.eval(rootFormData || {}, '', {
            root: rootFormData,
            formContext,
        })
    ), [dataValue, rootFormData, formContext]);

    useEffect(() => {
        if (!options) {
            return;
        }

        const object = (options || []).filter(option => {
            const parsedValue = convertIfNumber(formData);
            return option.id === parsedValue;
        })[0]?.item;

        if (!objectPath) {
            return;
        }

        setFormDataValues({ [objectPath]: object });
    }, [dataValue, options, objectPath]);

    // Detect and handle circular references (In create new shift, location lookup needs deep comparison to avoid multiple rerenders.)
    function safeStringify(obj, replacer = null, space = 2) {
        const cache = new Set();
        const result = JSON.stringify(obj, (key, value) => {
            if (typeof value === 'object' && value !== null) {
                if (cache.has(value)) {
                    // Circular reference found, return a placeholder string
                    return '[Circular]';
                }
                cache.add(value);
            }
            return value;
        }, space);
        cache.clear();
        return result;
    }

    const memoizedOptions = useMemo(() => options, [safeStringify(options)]);
    const memoizedDataValue = useMemo(() => dataValue, [safeStringify(dataValue)]);
    const memoizedOnSelectJnx = useCallback(onSelectJnx, [onSelectJnx]);
    const memoizedOnChangeJnx = useCallback(onChangeJnx, [onChangeJnx]);
    

    useEffect(() => {
        if (!memoizedOptions) {
            return;
        }

        const object = (memoizedOptions || []).filter(option => {
            const parsedValue = convertIfNumber(memoizedDataValue);
            return option.id === parsedValue;
        })[0]?.item;
        
        if (object && dataPath && memoizedOnSelectJnx) {
            setTimeout(() => {
                memoizedOnSelectJnx.eval(object, '', {...bindings, fieldPath: dataPath, object });
            }, 500);
        }
        
        if (clickFunction && object && dataPath && memoizedOnChangeJnx){
            setTimeout(() => {
                const result = memoizedOnChangeJnx.eval(object, '', {...bindings, fieldPath: dataPath, object });
                // Handle multiple function execution if result is an array (comma-separated expressions)
                // Example: "expr": "[$fn1(), $fn2()]" 
                // Example2: "expr": "[$fn1(), $set({'some.path.value', $fn2()})]"
                if (Array.isArray(result)) {
                    result.forEach(fn => {
                        if (typeof fn === 'function') fn();
                    });
                }
                setClickFunction(false);
            }, 500);
        }
    }, [memoizedOptions, memoizedDataValue, memoizedOnSelectJnx, memoizedOnChangeJnx]);

    const isDisabled = readonly || readOnlyIf || disabled;

    const fSelected = useMemo(() => {
        return dataValue ? (typeof(dataValue) === "object" && idJnx ? idJnx.eval(dataValue) : dataValue ) : ((firstSelected && !dataValue && idJnx && options) ? idJnx.eval(options?.[0]) : null)  
    }, [firstSelected, dataValue, idJnx, options]);

    const onChange = ({ target: { value } }) => {
        setClickFunction(true);
        setDataValue(parseFormData(value));
    };

    const selectOnChange = ( value ) => {
        setClickFunction(true);
        setDataValue(value);
    };

    // Custom component to show tooltip in the selected value. It look for email and phone in the metadata.
    const ValueContainer = useCallback((props) => {
        const { children, selectProps } = props;
        const selectedOption = selectProps.value;

        let tooltipText = null;
        if (selectedOption?.item?.metadata) {
            const tooltipParts = [];

            if (selectedOption.item.metadata.email) {
                tooltipParts.push(`Email: ${selectedOption.item.metadata.email}`);
            }

            const phoneNumber = selectedOption.item.metadata.phone;
            if (phoneNumber) {
                tooltipParts.push(`Tel: ${phoneNumber}`);
            }

            tooltipText = tooltipParts.length > 0 ? tooltipParts.join('\n') : null;
        }

        return (
            <div title={tooltipText || undefined}>
                <components.ValueContainer {...props}>
                    {children}
                </components.ValueContainer>
            </div>
        );
    }, []);

    const formatOptionLabel = useCallback((option, { context }) => {
        if (context !== 'menu') {
            return option.label;
        }

        const tooltipParts = [];

        if (option.item?.metadata?.email) {
            tooltipParts.push(`Email: ${option.item.metadata.email}`);
        }

        const phoneNumber = option.item?.metadata?.phone;
        if (phoneNumber) {
            tooltipParts.push(`Tel: ${phoneNumber}`);
        }

        const tooltipText = tooltipParts.length > 0 ? tooltipParts.join('\n') : null;

        return (
            <div
                title={tooltipText || undefined}
                style={{ width: '100%' }}
            >
                {option.label}
            </div>
        );
    }, []);

    return (
        <FormGroup disabled={readonly || readOnlyIf || disabled} style={containerStyle}>
            {title !== " " ? <label className="control-label" htmlFor="root_preferredBranchId">
                {title}{required ? <span className="required">*</span> : null}
            </label> : null}
            {loadingOptions ? (<div>
                <Loader>Loading {resource}</Loader>
            </div>) : (errorLoadingOptions ? (<Notification color="danger">
                <div>{errorLoadingOptions.message}</div>
            </Notification>) : ((parentId && collapsable) ? (<CollapsableTreeSelect
                disabled={isDisabled} defaultCollapseLevel={collapseLevel}
                required={required}
                value={dataValue}
                rootNodes={options}
                onChange={onChange}
            />) : 
                ( uiSearch ?
                    (
                        <Select
                            data-cy={$id}
                            isSearchable
                            value={
                                options.find(element => element.value === dataValue)
                            }
                            required={required}
                            isDisabled={readonly || disabled}
                            onChange={(value) => selectOnChange(parseFormData(value.value))}
                            options={
                                options
                            }
                            formatOptionLabel={formatOptionLabel}
                            components={{ ValueContainer }}
                        />
                    ): (
                        <Input type="select" disabled={isDisabled} data-cy={$id} required={required} value={fSelected ?? ""}  onChange={onChange}>
                            {allowNoValue ? <option value={undefined}>{noValue}</option> : null}
                            {(options || []).map((option, idx) => (
                                <option key={idx} value={idJnx ? idJnx.eval(option.item) : option.id}>{labelJnx ? labelJnx.eval(option.item) : option.label}</option>
                            ))}
                        </Input>
                    )
                )
            ))}
        </FormGroup>
    );
}

export function useParsedOptions(options, {
    parentId, id, label
}) {
    const tsOptions = useMemo(() => {
        if (!options) return null;
        const tsOptions = options.map(item => makeTreeNode(item, id, label, parentId));

        if (parentId) {
            const rootNodes = organizeTreeNodes(tsOptions);
            visitTrees(rootNodes);
            tsOptions.sort((a, b) => a.visitIdx - b.visitIdx);
            return rootNodes;
        }

        return tsOptions;
    }, [options, parentId]);
    return tsOptions;
}

export default LookupFormField;